<script lang="ts">
	import { onMount } from 'svelte';
	import Button from './Button.svelte';
	import Fa from 'svelte-fa';
	import { faCrop, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';

	interface Props {
		imageFile: File;
		targetWidth: number;
		targetHeight: number;
		title?: string;
		oncropped?: (detail: { dataUrl: string | ArrayBuffer | null; blob: Blob }) => void;
		oncancel?: () => void;
	}

	let {
		imageFile,
		targetWidth,
		targetHeight,
		title = 'Crop Image',
		oncropped,
		oncancel
	}: Props = $props();

	let canvas: HTMLCanvasElement = $state()!;
	let ctx: CanvasRenderingContext2D;
	let img: HTMLImageElement;
	let isDragging = false;
	let isResizing = false;
	let resizeHandle = '';
	let cropArea = { x: 0, y: 0, width: 0, height: 0 };
	let startPos = { x: 0, y: 0 };
	let startCropArea = { x: 0, y: 0, width: 0, height: 0 };
	let scale = 1;
	let canvasWidth = $state(600);
	let canvasHeight = $state(400);

	// Responsive canvas sizing
	function getResponsiveCanvasSize() {
		if (typeof window !== 'undefined') {
			const maxWidth = Math.min(600, window.innerWidth - 32); // 32px for padding
			const maxHeight = Math.min(400, window.innerHeight * 0.5);
			return { width: maxWidth, height: maxHeight };
		}
		return { width: 600, height: 400 };
	}

	onMount(() => {
		ctx = canvas.getContext('2d')!;
		const size = getResponsiveCanvasSize();
		canvasWidth = size.width;
		canvasHeight = size.height;
		loadImage();

		// Update canvas size on window resize
		const handleResize = () => {
			const newSize = getResponsiveCanvasSize();
			if (newSize.width !== canvasWidth || newSize.height !== canvasHeight) {
				canvasWidth = newSize.width;
				canvasHeight = newSize.height;
				loadImage();
			}
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	});

	function loadImage() {
		img = new Image();
		img.onload = () => {
			// Calculate scale to fit image in canvas while maintaining aspect ratio
			const scaleX = canvasWidth / img.width;
			const scaleY = canvasHeight / img.height;
			scale = Math.min(scaleX, scaleY, 1);

			const scaledWidth = img.width * scale;
			const scaledHeight = img.height * scale;

			// Center the image
			const offsetX = (canvasWidth - scaledWidth) / 2;
			const offsetY = (canvasHeight - scaledHeight) / 2;

			// Initialize crop area to target aspect ratio
			const aspectRatio = targetWidth / targetHeight;
			let cropWidth = Math.min(scaledWidth, scaledHeight * aspectRatio);
			let cropHeight = cropWidth / aspectRatio;

			if (cropHeight > scaledHeight) {
				cropHeight = scaledHeight;
				cropWidth = cropHeight * aspectRatio;
			}

			cropArea = {
				x: offsetX + (scaledWidth - cropWidth) / 2,
				y: offsetY + (scaledHeight - cropHeight) / 2,
				width: cropWidth,
				height: cropHeight
			};

			drawCanvas();
		};

		const reader = new FileReader();
		reader.onload = (e) => {
			img.src = e.target?.result as string;
		};
		reader.readAsDataURL(imageFile);
	}

	function drawCanvas() {
		if (!img || !ctx) return;

		// Clear canvas
		ctx.clearRect(0, 0, canvasWidth, canvasHeight);

		// Draw image
		const scaledWidth = img.width * scale;
		const scaledHeight = img.height * scale;
		const offsetX = (canvasWidth - scaledWidth) / 2;
		const offsetY = (canvasHeight - scaledHeight) / 2;

		ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

		// Draw overlay (darken everything except crop area)
		ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
		ctx.fillRect(0, 0, canvasWidth, canvasHeight);

		// Clear crop area
		ctx.clearRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);

		// Redraw image in crop area
		const sourceX = (cropArea.x - offsetX) / scale;
		const sourceY = (cropArea.y - offsetY) / scale;
		const sourceWidth = cropArea.width / scale;
		const sourceHeight = cropArea.height / scale;

		ctx.drawImage(
			img,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			cropArea.x,
			cropArea.y,
			cropArea.width,
			cropArea.height
		);

		// Draw crop area border
		ctx.strokeStyle = '#3b82f6';
		ctx.lineWidth = 2;
		ctx.strokeRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);

		// Draw corner and edge handles (larger on mobile for better touch targets)
		const handleSize = typeof window !== 'undefined' && window.innerWidth < 640 ? 20 : 12;
		ctx.fillStyle = '#3b82f6';

		// Corner handles
		const corners = [
			{ x: cropArea.x, y: cropArea.y, cursor: 'nw-resize', handle: 'nw' },
			{ x: cropArea.x + cropArea.width, y: cropArea.y, cursor: 'ne-resize', handle: 'ne' },
			{ x: cropArea.x, y: cropArea.y + cropArea.height, cursor: 'sw-resize', handle: 'sw' },
			{
				x: cropArea.x + cropArea.width,
				y: cropArea.y + cropArea.height,
				cursor: 'se-resize',
				handle: 'se'
			}
		];

		// Edge handles
		const edges = [
			{ x: cropArea.x + cropArea.width / 2, y: cropArea.y, cursor: 'n-resize', handle: 'n' },
			{
				x: cropArea.x + cropArea.width / 2,
				y: cropArea.y + cropArea.height,
				cursor: 's-resize',
				handle: 's'
			},
			{ x: cropArea.x, y: cropArea.y + cropArea.height / 2, cursor: 'w-resize', handle: 'w' },
			{
				x: cropArea.x + cropArea.width,
				y: cropArea.y + cropArea.height / 2,
				cursor: 'e-resize',
				handle: 'e'
			}
		];

		[...corners, ...edges].forEach((handle) => {
			ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
		});
	}

	function getHandleAtPosition(x: number, y: number): string {
		// Much larger touch tolerance on mobile for easier interaction
		const tolerance = typeof window !== 'undefined' && window.innerWidth < 640 ? 30 : 8;

		// Check corners first (they have priority over edges)
		const corners = [
			{ x: cropArea.x, y: cropArea.y, handle: 'nw' },
			{ x: cropArea.x + cropArea.width, y: cropArea.y, handle: 'ne' },
			{ x: cropArea.x, y: cropArea.y + cropArea.height, handle: 'sw' },
			{ x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height, handle: 'se' }
		];

		for (const corner of corners) {
			if (Math.abs(x - corner.x) <= tolerance && Math.abs(y - corner.y) <= tolerance) {
				return corner.handle;
			}
		}

		// Check edges
		const edges = [
			{ x: cropArea.x + cropArea.width / 2, y: cropArea.y, handle: 'n' },
			{ x: cropArea.x + cropArea.width / 2, y: cropArea.y + cropArea.height, handle: 's' },
			{ x: cropArea.x, y: cropArea.y + cropArea.height / 2, handle: 'w' },
			{ x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height / 2, handle: 'e' }
		];

		for (const edge of edges) {
			if (Math.abs(x - edge.x) <= tolerance && Math.abs(y - edge.y) <= tolerance) {
				return edge.handle;
			}
		}

		return '';
	}

	function getCursorForHandle(handle: string): string {
		const cursors: Record<string, string> = {
			nw: 'nw-resize',
			ne: 'ne-resize',
			sw: 'sw-resize',
			se: 'se-resize',
			n: 'n-resize',
			s: 's-resize',
			w: 'w-resize',
			e: 'e-resize'
		};
		return cursors[handle] || 'move';
	}

	function getEventPosition(e: MouseEvent | TouchEvent) {
		const rect = canvas.getBoundingClientRect();
		// Account for canvas scaling (CSS size vs canvas resolution)
		const scaleX = canvasWidth / rect.width;
		const scaleY = canvasHeight / rect.height;

		if ('touches' in e) {
			const touch = e.touches[0];
			return {
				x: (touch.clientX - rect.left) * scaleX,
				y: (touch.clientY - rect.top) * scaleY
			};
		}
		return {
			x: (e.clientX - rect.left) * scaleX,
			y: (e.clientY - rect.top) * scaleY
		};
	}

	function handleMouseDown(e: MouseEvent) {
		handlePointerDown(e);
	}

	function handleTouchStart(e: TouchEvent) {
		e.preventDefault();
		handlePointerDown(e);
	}

	function handlePointerDown(e: MouseEvent | TouchEvent) {
		const { x, y } = getEventPosition(e);

		// Check if clicking on a resize handle
		const handle = getHandleAtPosition(x, y);

		if (handle) {
			isResizing = true;
			resizeHandle = handle;
			startPos = { x, y };
			startCropArea = { ...cropArea };
		} else if (
			x >= cropArea.x &&
			x <= cropArea.x + cropArea.width &&
			y >= cropArea.y &&
			y <= cropArea.y + cropArea.height
		) {
			// Click inside crop area - start dragging
			isDragging = true;
			startPos = { x: x - cropArea.x, y: y - cropArea.y };
		}
	}

	function handleMouseMove(e: MouseEvent) {
		handlePointerMove(e);
	}

	function handleTouchMove(e: TouchEvent) {
		e.preventDefault();
		handlePointerMove(e);
	}

	function handlePointerMove(e: MouseEvent | TouchEvent) {
		const { x, y } = getEventPosition(e);

		if (isResizing) {
			const deltaX = x - startPos.x;
			const deltaY = y - startPos.y;
			const aspectRatio = targetWidth / targetHeight;

			let newCropArea = { ...startCropArea };

			// Handle different resize directions
			switch (resizeHandle) {
				case 'nw':
					newCropArea.width = startCropArea.width - deltaX;
					newCropArea.height = newCropArea.width / aspectRatio;
					newCropArea.x = startCropArea.x + deltaX;
					newCropArea.y = startCropArea.y + startCropArea.height - newCropArea.height;
					break;
				case 'ne':
					newCropArea.width = startCropArea.width + deltaX;
					newCropArea.height = newCropArea.width / aspectRatio;
					newCropArea.y = startCropArea.y + startCropArea.height - newCropArea.height;
					break;
				case 'sw':
					newCropArea.width = startCropArea.width - deltaX;
					newCropArea.height = newCropArea.width / aspectRatio;
					newCropArea.x = startCropArea.x + deltaX;
					break;
				case 'se':
					newCropArea.width = startCropArea.width + deltaX;
					newCropArea.height = newCropArea.width / aspectRatio;
					break;
				case 'n':
					newCropArea.height = startCropArea.height - deltaY;
					newCropArea.width = newCropArea.height * aspectRatio;
					newCropArea.x = startCropArea.x + (startCropArea.width - newCropArea.width) / 2;
					newCropArea.y = startCropArea.y + deltaY;
					break;
				case 's':
					newCropArea.height = startCropArea.height + deltaY;
					newCropArea.width = newCropArea.height * aspectRatio;
					newCropArea.x = startCropArea.x + (startCropArea.width - newCropArea.width) / 2;
					break;
				case 'w':
					newCropArea.width = startCropArea.width - deltaX;
					newCropArea.height = newCropArea.width / aspectRatio;
					newCropArea.x = startCropArea.x + deltaX;
					newCropArea.y = startCropArea.y + (startCropArea.height - newCropArea.height) / 2;
					break;
				case 'e':
					newCropArea.width = startCropArea.width + deltaX;
					newCropArea.height = newCropArea.width / aspectRatio;
					newCropArea.y = startCropArea.y + (startCropArea.height - newCropArea.height) / 2;
					break;
			}

			// Constrain to minimum size and canvas bounds
			const minSize = 50;
			if (
				newCropArea.width >= minSize &&
				newCropArea.height >= minSize &&
				newCropArea.x >= 0 &&
				newCropArea.y >= 0 &&
				newCropArea.x + newCropArea.width <= canvasWidth &&
				newCropArea.y + newCropArea.height <= canvasHeight
			) {
				cropArea = newCropArea;
			}

			drawCanvas();
		} else if (isDragging) {
			// Update crop area position
			const newX = x - startPos.x;
			const newY = y - startPos.y;

			// Constrain to canvas bounds
			cropArea.x = Math.max(0, Math.min(canvasWidth - cropArea.width, newX));
			cropArea.y = Math.max(0, Math.min(canvasHeight - cropArea.height, newY));

			drawCanvas();
		} else {
			// Update cursor based on hover position
			const handle = getHandleAtPosition(x, y);
			if (handle) {
				canvas.style.cursor = getCursorForHandle(handle);
			} else if (
				x >= cropArea.x &&
				x <= cropArea.x + cropArea.width &&
				y >= cropArea.y &&
				y <= cropArea.y + cropArea.height
			) {
				canvas.style.cursor = 'move';
			} else {
				canvas.style.cursor = 'default';
			}
		}
	}

	function handleMouseUp() {
		handlePointerUp();
	}

	function handleTouchEnd(e: TouchEvent) {
		e.preventDefault();
		handlePointerUp();
	}

	function handlePointerUp() {
		isDragging = false;
		isResizing = false;
		resizeHandle = '';
		canvas.style.cursor = 'default';
	}

	function handleWheel(e: WheelEvent) {
		e.preventDefault();
		// Wheel functionality removed - use draggable borders instead
	}

	function cropImage() {
		// Create a new canvas for the cropped image
		const cropCanvas = document.createElement('canvas');
		cropCanvas.width = targetWidth;
		cropCanvas.height = targetHeight;
		const cropCtx = cropCanvas.getContext('2d')!;

		// Calculate source coordinates in original image
		const scaledWidth = img.width * scale;
		const scaledHeight = img.height * scale;
		const offsetX = (canvasWidth - scaledWidth) / 2;
		const offsetY = (canvasHeight - scaledHeight) / 2;

		const sourceX = (cropArea.x - offsetX) / scale;
		const sourceY = (cropArea.y - offsetY) / scale;
		const sourceWidth = cropArea.width / scale;
		const sourceHeight = cropArea.height / scale;

		// Draw cropped image to new canvas
		cropCtx.drawImage(
			img,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			0,
			0,
			targetWidth,
			targetHeight
		);

		// Convert to blob and dispatch
		cropCanvas.toBlob((blob) => {
			if (blob) {
				const reader = new FileReader();
				reader.onload = () => {
					oncropped?.({
						dataUrl: reader.result,
						blob: blob
					});
				};
				reader.readAsDataURL(blob);
			}
		}, 'image/png');
	}

	function cancel() {
		oncancel?.();
	}
</script>

<div class="w-full max-w-4xl card bg-surface-50-950">
	<div class="card-header px-3 py-2 sm:px-4 sm:py-3">
		<div class="flex items-center justify-between">
			<h3 class="flex items-center space-x-1 h4 sm:space-x-2 sm:h3">
				<Fa icon={faCrop} class="text-base sm:text-xl" />
				<span class="text-sm sm:text-base">{title}</span>
			</h3>
			<button
				type="button"
				class="inline-flex h-7 w-7 items-center justify-center rounded-full border border-surface-500 preset-tonal-surface"
				onclick={cancel}
			>
				<Fa icon={faTimes} class="text-xs" />
			</button>
		</div>
	</div>

	<div class="card-body p-3 sm:p-6">
		<div class="space-y-2 sm:space-y-4">
			<div class="px-2 text-center">
				<p class="text-xs text-surface-700-300 sm:text-sm">
					Drag to move the crop area. Drag the corners and edges to resize. Target size: {targetWidth}
					× {targetHeight}px
				</p>
			</div>

			<div class="flex justify-center overflow-x-auto px-2">
				<canvas
					bind:this={canvas}
					width={canvasWidth}
					height={canvasHeight}
					class="max-w-full cursor-move touch-none rounded-lg border border-surface-300"
					onmousedown={handleMouseDown}
					onmousemove={handleMouseMove}
					onmouseup={handleMouseUp}
					ontouchstart={handleTouchStart}
					ontouchmove={handleTouchMove}
					ontouchend={handleTouchEnd}
					onwheel={handleWheel}
				></canvas>
			</div>

			<div class="flex justify-center space-x-2 px-2 sm:space-x-4">
				<Button variant="secondary" classes="border border-surface-500" onclick={cancel}>
					<Fa icon={faTimes} class="mr-1 text-sm sm:mr-2 sm:text-base" />
					<span class="text-sm sm:text-base">Cancel</span>
				</Button>
				<Button variant="primary" onclick={cropImage}>
					<Fa icon={faCheck} class="mr-1 text-sm sm:mr-2 sm:text-base" />
					<span class="text-sm sm:text-base">Apply Crop</span>
				</Button>
			</div>
		</div>
	</div>
</div>
